import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";

import { isFirstSlide } from "./presentationSlides";

import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";

/** Screen pixels between a frame's top edge and its button row. */
const ROW_GAP = 8;
/** Screen pixels between the last frame and the add button. */
const ADD_GAP = 16;

export type PresentationSlideControlsProps = {
  /** The slides, in slide order. */
  frames: readonly ExcalidrawElement[];
  /** The slide the pointer is over or whose drawing is selected. */
  activeFrameId: string | null;
  /** Scene coordinates to screen pixels (`screenX = (sceneX + scrollX) * zoom`). */
  toScreen(x: number, y: number): { x: number; y: number };
  zoom: number;
  /** False at the slide limit. */
  canAdd: boolean;
  onAdd(): void;
  onSwap(frameId: string, direction: -1 | 1): void;
  onDelete(frameId: string): void;
};

/**
 * The slide buttons of a presentation canvas, hung on the canvas as screen
 * overlays: 「+」 just right of the last slide, and move/move/delete above the
 * active slide. Frames are never dragged — these buttons are the only way to
 * lay the row out. Like every overlay here they never join `elements`, so
 * they stay out of exports and prints.
 */
export const PresentationSlideControls = ({
  frames,
  activeFrameId,
  toScreen,
  zoom,
  canAdd,
  onAdd,
  onSwap,
  onDelete,
}: PresentationSlideControlsProps) => {
  if (frames.length === 0) {
    return null;
  }
  const last = frames[frames.length - 1];
  const addAt = toScreen(last.x + last.width, last.y + last.height / 2);
  const activeIndex = frames.findIndex((frame) => frame.id === activeFrameId);
  const active = activeIndex >= 0 ? frames[activeIndex] : null;
  const rowAt = active ? toScreen(active.x + active.width, active.y) : null;

  return (
    <div
      className="canvaschool-slide-controls"
      // Not a drawing gesture: keep the core from starting one underneath.
      onPointerDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="canvaschool-slide-button canvaschool-slide-add"
        aria-label="슬라이드 더하기"
        title={
          canAdd ? "슬라이드 더하기" : "슬라이드는 30장까지 만들 수 있어요."
        }
        disabled={!canAdd}
        style={{
          left: `${addAt.x + ADD_GAP * Math.min(zoom, 1)}px`,
          top: `${addAt.y}px`,
        }}
        onClick={onAdd}
      >
        <Plus size={20} aria-hidden="true" />
      </button>
      {active && rowAt && (
        <div
          className="canvaschool-slide-row"
          role="group"
          aria-label={`${activeIndex + 1}번 슬라이드`}
          style={{
            left: `${rowAt.x}px`,
            top: `${rowAt.y - ROW_GAP}px`,
          }}
        >
          <button
            type="button"
            className="canvaschool-slide-button"
            aria-label="슬라이드를 앞으로"
            title="앞으로 옮기기"
            disabled={activeIndex === 0}
            onClick={() => onSwap(active.id, -1)}
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="canvaschool-slide-button"
            aria-label="슬라이드를 뒤로"
            title="뒤로 옮기기"
            disabled={activeIndex === frames.length - 1}
            onClick={() => onSwap(active.id, 1)}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="canvaschool-slide-button canvaschool-slide-delete"
            aria-label="슬라이드 지우기"
            title={
              isFirstSlide(active)
                ? "첫 슬라이드는 지울 수 없어요"
                : "슬라이드 지우기"
            }
            disabled={frames.length < 2 || isFirstSlide(active)}
            onClick={() => onDelete(active.id)}
          >
            <Trash2 size={15} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};
